import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  COMPANY_LIST_PAGE_SIZE,
  CompanyListLoadOptions,
  SortDescriptor,
} from './company-list.request';
import { LazyPage } from './lazy-page';
import { PageCache } from './page-cache';

/**
 * A chunk without the count that took longer than this is not followed by a
 * prefetch: the next one would most likely take as long and load the
 * database for rows that may never be shown. The first chunk of a query is
 * exempt, the count makes it slow for a broad search while the chunks after it
 * skip the count.
 */
export const PREFETCH_MAX_DURATION_MS = 10000;

/** How many chunks load at a time on the way to a row further down. */
export const CHUNK_CONCURRENCY = 4;

export interface CompanyListSourceOptions<T> {
  /**
   * The loaded chunks, shared by the sources of the same kind, so a new
   * source for the same list or search finds them. Without one, the source
   * keeps its own.
   */
  cache?: PageCache<LazyPage<T>>;
  /** Tells the queries of different lists apart in a shared cache. */
  scope?: string;
  /** Loads the next chunk in the background once a chunk was added. */
  prefetch?: boolean;
}

/** What the requests of one query need: the cache key, filter and sort. */
interface Query {
  key: string;
  filter: unknown[] | null;
  sort: SortDescriptor[] | null;
}

/**
 * The companies of a list or a search, loaded from the server in chunks of
 * the page size of the API while the user scrolls: the data source of the
 * list detail page and the search. `items` holds the rows loaded so far, from
 * the first row on; `loadMore()` adds the next chunk, `ensureLoaded()` the
 * chunks down to a row. The company page reads rows by their absolute index
 * (`rowAt()`), which loads the chunk of a row further down on its own.
 *
 * Loaded chunks stay in a cache per query (scope, filter, sorting and page
 * size): a cached chunk is added at once, without a request. With `prefetch`,
 * the chunk after the last added one loads in the background, so the rows are
 * usually there before the user scrolls to the end. A chunk already in flight
 * is not requested again, and the chunks after the first one of a query skip
 * the count, which is known by then. Another filter or sorting drops the
 * chunks of the former query; `load()` starts the new query at its first row.
 *
 * Only the newest query updates the rows, so a slow response never mixes into
 * a newer query. A row that an earlier chunk had already (rows with equal sort
 * values may change places between requests) is not added twice.
 */
export class CompanyListSource<T extends object> {
  /** The loaded rows from the first one on; replaced, never changed. */
  public items: T[] = [];
  public totalRecords = 0;
  /** The first chunk of the query failed; the table shows no rows. */
  public error: string | null = null;
  /** A further chunk failed; the loaded rows stay. */
  public moreError: string | null = null;
  /** The first chunk of a query, or a refresh of the loaded rows, loads. */
  public readonly loading$ = new BehaviorSubject<boolean>(false);
  /** More rows load at the end of the loaded ones. */
  public readonly loadingMore$ = new BehaviorSubject<boolean>(false);

  private filter: unknown[] | null = null;
  private sort: SortDescriptor[] | null = null;
  /** The query of the loaded rows. */
  private query: Query | null = null;
  private loaded = false;
  private complete = false;
  /** Number of chunks the rows came from. */
  private chunks = 0;
  private keys = new Set<unknown>();
  /** Counts the queries; the answers of a former one are dropped. */
  private run = 0;
  private loadedGeneration = -1;
  private lastLoad: Promise<T[]> | null = null;
  private moreLoad: Promise<void> | null = null;
  /** The request of the next chunk in flight. */
  private moreToken: object | null = null;
  private loadingCount = 0;
  private moreCount = 0;
  private readonly cache: PageCache<LazyPage<T>>;
  private readonly scope: string;
  private readonly prefetchChunks: boolean;

  constructor(
    private fetch: (options: CompanyListLoadOptions) => Observable<LazyPage<T>>,
    public readonly key = 'business_id',
    public readonly pageSize = COMPANY_LIST_PAGE_SIZE,
    options: CompanyListSourceOptions<T> = {}
  ) {
    this.cache = options.cache ?? new PageCache<LazyPage<T>>();
    this.scope = options.scope ?? '';
    this.prefetchChunks = options.prefetch ?? false;
  }

  public get loading() {
    return this.loading$.value;
  }

  public get loadingMore() {
    return this.loadingMore$.value;
  }

  public get isLoaded() {
    return this.loaded;
  }

  /** All rows of the query are loaded. */
  public get isComplete() {
    return this.loaded && this.complete;
  }

  /**
   * The rows were loaded before companies changed (the cache was cleared
   * since); they are refreshed before they are shown again.
   */
  public get isStale() {
    return this.loaded && this.loadedGeneration !== this.cache.generation;
  }

  /** The filter of the requests, to tell whether the source fits a page. */
  public get currentFilter() {
    return this.filter;
  }

  public get currentSort() {
    return this.sort;
  }

  /** The newest request, or a new one when nothing was requested yet. */
  public whenLoaded(): Promise<T[]> {
    return this.lastLoad ?? this.load();
  }

  public setFilter(filter: unknown[] | null) {
    this.filter = filter;
    this.dropFormerQuery();
  }

  public setSort(sort: SortDescriptor[] | null) {
    this.sort = sort && sort.length ? sort : null;
    this.dropFormerQuery();
  }

  /** Drops the cached chunks of the current query, for a source left behind. */
  public dropCache() {
    this.cache.drop(this.queryKey());
  }

  /**
   * The rows of the current filter and sorting without a request when they
   * are loaded and fresh; stale rows are refreshed, another query loads from
   * its first row.
   */
  public open(): Promise<T[]> {
    const sameQuery = this.query?.key === this.queryKey();
    if (sameQuery && this.loading && this.lastLoad) {
      return this.lastLoad;
    }
    if (sameQuery && this.loaded && !this.error) {
      if (this.isStale) {
        return this.refresh();
      }
      return (this.lastLoad = Promise.resolve(this.items));
    }
    return this.load();
  }

  /**
   * Starts the query at its first row. The former rows stay until the first
   * chunk arrives (at once when it is cached).
   */
  public load(): Promise<T[]> {
    const run = this.startRun();
    const query = this.query as Query;
    const cached = this.cache.get(this.chunkKey(query, 0));
    if (cached) {
      this.showChunks([cached]);
      this.prefetch(query, run, 0);
      return (this.lastLoad = Promise.resolve(this.items));
    }
    const started = Date.now();
    const counting = this.cache.total(query.key) === undefined;
    this.setLoading(1);
    // The rows change with the response, before the promise settles.
    const promise = this.request(query, 0, {
      next: (page) => {
        if (run === this.run) {
          this.showChunks([page]);
        }
        this.setLoading(-1);
        if (counting || Date.now() - started <= PREFETCH_MAX_DURATION_MS) {
          this.prefetch(query, run, 0);
        }
      },
      error: (e) => {
        if (run === this.run) {
          this.error = e.message;
          this.items = [];
          this.keys.clear();
          this.chunks = 0;
          this.loaded = true;
          this.complete = true;
        }
        this.setLoading(-1);
      },
    }).then((page) => page.data);
    this.lastLoad = promise;
    return promise;
  }

  /**
   * Loads the chunks shown so far again, with the count, after companies
   * changed (e.g. removed from the list), and replaces the rows at once, so
   * the table keeps its scroll position.
   */
  public refresh(): Promise<T[]> {
    const count = Math.max(1, this.chunks);
    const run = this.startRun();
    const query = this.query as Query;
    this.setLoading(1);
    const promise = firstValueFrom(this.fetchChunk(query, 0))
      .then((first) =>
        this.fetchChunks(
          query,
          Array.from({ length: count - 1 }, (_, k) => k + 1).filter(
            (chunk) => chunk * this.pageSize < (first.totalCount ?? 0)
          )
        ).then((rest) => [first, ...rest])
      )
      .then((pages) => {
        if (run === this.run) {
          this.showChunks(pages);
        }
        return this.items;
      })
      .catch((e: Error) => {
        if (run === this.run) {
          this.error = e.message;
        }
        throw e;
      })
      .finally(() => this.setLoading(-1));
    this.lastLoad = promise;
    return promise;
  }

  /**
   * Adds the next chunk, from the cache at once or from the server. Nothing
   * happens while the first chunk loads, when all rows are there or the next
   * chunk is on its way already.
   */
  public loadMore(): Promise<void> {
    const query = this.query;
    if (!query || !this.loaded || this.complete || this.loading || this.error) {
      return Promise.resolve();
    }
    if (this.moreToken && this.moreLoad) {
      return this.moreLoad;
    }
    const run = this.run;
    const chunk = this.chunks;
    const cached = this.cache.get(this.chunkKey(query, chunk));
    if (cached) {
      this.addChunk(chunk, cached);
      this.prefetch(query, run, chunk);
      return Promise.resolve();
    }
    this.moreError = null;
    this.setLoadingMore(1);
    const started = Date.now();
    const token = {};
    this.moreToken = token;
    const done = () => {
      if (this.moreToken === token) {
        this.moreToken = null;
        this.moreLoad = null;
      }
      this.setLoadingMore(-1);
    };
    const promise: Promise<void> = this.request(query, chunk, {
      next: (page) => {
        if (run === this.run) {
          this.addChunk(chunk, page);
        }
        done();
        if (Date.now() - started <= PREFETCH_MAX_DURATION_MS) {
          this.prefetch(query, run, chunk);
        }
      },
      error: (e) => {
        if (run === this.run) {
          this.moreError = e.message;
        }
        done();
      },
    }).then(
      () => undefined,
      () => undefined
    );
    if (this.moreToken === token) {
      // Still on its way.
      this.moreLoad = promise;
    }
    return promise;
  }

  /**
   * Loads the chunks down to the row with this index, a few at a time, for
   * the way back to the company opened last. Resolves when the row is there,
   * the list ends before it, or a chunk failed.
   */
  public async ensureLoaded(index: number): Promise<void> {
    await this.whenLoaded().catch(() => undefined);
    const query = this.query;
    if (!query || index < this.items.length || this.complete || this.error) {
      return;
    }
    const run = this.run;
    const last = Math.floor(index / this.pageSize);
    const total = this.totalRecords;
    const wanted = Array.from(
      { length: last - this.chunks + 1 },
      (_, k) => this.chunks + k
    ).filter((chunk) => !total || chunk * this.pageSize < total);
    this.moreError = null;
    this.setLoadingMore(1);
    try {
      const pages = await this.fetchChunks(query, wanted);
      if (run === this.run) {
        pages.forEach((page, k) => this.addChunk(wanted[k], page));
        this.prefetch(query, run, this.chunks - 1);
      }
    } catch (e) {
      if (run === this.run) {
        this.moreError = (e as Error).message;
      }
    } finally {
      this.setLoadingMore(-1);
    }
  }

  /**
   * The row with this index in the whole list: a loaded one, or one of a
   * chunk further down, which is loaded (and added when it is the next one).
   * Null past the end, or when its chunk failed.
   */
  public async rowAt(index: number): Promise<T | null> {
    if (index < 0) {
      return null;
    }
    await this.whenLoaded().catch(() => undefined);
    if (index < this.items.length) {
      return this.items[index];
    }
    const query = this.query;
    if (!query || this.complete || this.error) {
      return null;
    }
    const chunk = Math.floor(index / this.pageSize);
    if (chunk === this.chunks) {
      await this.loadMore();
      return this.items[index] ?? null;
    }
    try {
      const page = await firstValueFrom(this.fetchChunk(query, chunk));
      return page.data[index - chunk * this.pageSize] ?? null;
    } catch {
      return null;
    }
  }

  /** Index of the loaded row with this key, -1 if it is not loaded. */
  public indexOf(key: unknown) {
    return this.items.findIndex(
      (row) => (row as Record<string, unknown>)[this.key] === key
    );
  }

  public clearError() {
    this.error = null;
    this.moreError = null;
  }

  private queryKey() {
    return JSON.stringify([this.scope, this.filter, this.sort, this.pageSize]);
  }

  private chunkKey(query: Query, chunk: number) {
    return `${query.key}#${chunk}`;
  }

  /** A new query run with the current filter and sorting. */
  private startRun() {
    const run = ++this.run;
    this.query = {
      key: this.queryKey(),
      filter: this.filter,
      sort: this.sort,
    };
    this.moreLoad = null;
    this.moreToken = null;
    this.moreError = null;
    return run;
  }

  private dropFormerQuery() {
    if (this.query !== null && this.query.key !== this.queryKey()) {
      this.cache.drop(this.query.key);
    }
  }

  private keyOf(row: T) {
    return (row as Record<string, unknown>)[this.key];
  }

  /** The rows of these chunks, from the first one on. */
  private showChunks(pages: LazyPage<T>[]) {
    this.error = null;
    this.items = [];
    this.keys.clear();
    this.chunks = 0;
    this.totalRecords = pages[0].totalCount ?? this.totalRecords;
    this.loaded = true;
    this.complete = false;
    this.loadedGeneration = this.cache.generation;
    pages.forEach((page, chunk) => this.addChunk(chunk, page));
    if (!pages.length) {
      this.complete = true;
    }
  }

  /** Adds the rows of the chunk after the loaded ones, once. */
  private addChunk(chunk: number, page: LazyPage<T>) {
    if (chunk !== this.chunks) {
      return;
    }
    const rows = page.data.filter((row) => !this.keys.has(this.keyOf(row)));
    rows.forEach((row) => this.keys.add(this.keyOf(row)));
    this.items = rows.length ? [...this.items, ...rows] : this.items;
    this.chunks++;
    this.totalRecords = page.totalCount ?? this.totalRecords;
    this.complete =
      page.data.length < this.pageSize ||
      this.chunks * this.pageSize >= this.totalRecords;
  }

  /** The chunk from the cache, the request in flight, or a new request. */
  private fetchChunk(query: Query, chunk: number): Observable<LazyPage<T>> {
    const known = this.cache.total(query.key);
    const options: CompanyListLoadOptions = {
      skip: chunk * this.pageSize,
      take: this.pageSize,
      sort: query.sort,
      filter: query.filter,
      ...(known !== undefined ? { skipCount: true } : {}),
    };
    return this.cache.fetch(this.chunkKey(query, chunk), () =>
      this.fetch(options).pipe(
        map((page) => {
          if (typeof page.totalCount === 'number') {
            this.cache.setTotal(query.key, page.totalCount);
            return page;
          }
          // An API without skipCount always counts.
          return { ...page, totalCount: known ?? 0 };
        })
      )
    );
  }

  /**
   * The chunk, with the handlers called synchronously with the response, so
   * the table shows the rows in the same turn.
   */
  private request(
    query: Query,
    chunk: number,
    handlers: { next: (page: LazyPage<T>) => void; error: (e: Error) => void }
  ): Promise<LazyPage<T>> {
    return new Promise<LazyPage<T>>((resolve, reject) => {
      let answered = false;
      this.fetchChunk(query, chunk).subscribe({
        next: (page) => {
          if (!answered) {
            answered = true;
            handlers.next(page);
            resolve(page);
          }
        },
        error: (e: Error) => {
          answered = true;
          handlers.error(e);
          reject(e);
        },
        complete: () => {
          if (!answered) {
            const e = new Error('No answer');
            handlers.error(e);
            reject(e);
          }
        },
      });
    });
  }

  /** These chunks, at most CHUNK_CONCURRENCY requests at a time, in order. */
  private async fetchChunks(
    query: Query,
    chunks: number[]
  ): Promise<LazyPage<T>[]> {
    const pages: LazyPage<T>[] = new Array(chunks.length);
    let next = 0;
    const worker = async () => {
      while (next < chunks.length) {
        const k = next++;
        pages[k] = await firstValueFrom(this.fetchChunk(query, chunks[k]));
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CHUNK_CONCURRENCY, chunks.length) }, worker)
    );
    return pages;
  }

  /**
   * Loads the chunk after `chunk` in the background, unless the query changed
   * meanwhile or all rows are there. Its failure stays silent; the chunk
   * loads again when the table needs it.
   */
  private prefetch(query: Query, run: number, chunk: number) {
    const next = chunk + 1;
    if (
      !this.prefetchChunks ||
      run !== this.run ||
      this.complete ||
      next * this.pageSize >= this.totalRecords
    ) {
      return;
    }
    const key = this.chunkKey(query, next);
    if (!this.cache.has(key) && !this.cache.isPending(key)) {
      this.fetchChunk(query, next).subscribe({ error: () => undefined });
    }
  }

  private setLoading(change: number) {
    this.loadingCount += change;
    const loading = this.loadingCount > 0;
    if (loading !== this.loading$.value) {
      this.loading$.next(loading);
    }
  }

  private setLoadingMore(change: number) {
    this.moreCount += change;
    const loading = this.moreCount > 0;
    if (loading !== this.loadingMore$.value) {
      this.loadingMore$.next(loading);
    }
  }
}
